import React, { useState, useRef } from "react";
import { Upload, FileText, Check, X, AlertCircle, Loader2, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

export default function ImportarVentas() {
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [extracted, setExtracted] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef();

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (newFiles.length === 0) {
      toast.error("Solo se permiten archivos PDF");
      return;
    }
    setFiles(prev => [...prev, ...newFiles]);
    setResults(null);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExtracted = (index) => {
    setExtracted(prev => prev.filter((_, i) => i !== index));
  };

  const processFiles = async () => {
    if (files.length === 0) {
      toast.error("Selecciona al menos un PDF");
      return;
    }

    setProcessing(true);
    setExtracted([]);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const formData = new FormData();
        formData.append("file", file);

        const res = await api.post("/import/aronium-pdf", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });

        if (res.data.success) {
          results.push({ ...res.data.data, _file_name: file.name, _status: "ok" });
        } else {
          results.push({ _file_name: file.name, _status: "error", _error: "No se pudo extraer" });
        }
      } catch (err) {
        results.push({ 
          _file_name: file.name, 
          _status: "error", 
          _error: err.response?.data?.detail || err.message 
        });
      }
    }

    setExtracted(results);
    setProcessing(false);
    setFiles([]);
    
    const okCount = results.filter(r => r._status === "ok").length;
    const errCount = results.filter(r => r._status === "error").length;
    
    if (okCount > 0) toast.success(`${okCount} PDF(s) procesados correctamente`);
    if (errCount > 0) toast.error(`${errCount} PDF(s) con errores`);
  };

  const confirmImport = async () => {
    const toImport = extracted.filter(e => e._status === "ok");
    if (toImport.length === 0) {
      toast.error("No hay ventas válidas para importar");
      return;
    }

    setImporting(true);
    try {
      const payload = toImport.map(e => ({
        folio: e.folio,
        date: e.date,
        customer_name: e.customer_name,
        customer_address: e.customer_address,
        customer_phone: e.customer_phone,
        items: e.items,
        subtotal: e.subtotal,
        tax: e.tax,
        shipping: e.shipping || 0,
        total: e.total,
        payment_status: e.payment_status,
        payment_method: e.payment_method,
        notes: e.notes,
      }));

      const res = await api.post("/import/aronium-confirm", payload);
      setResults(res.data);
      setExtracted([]);
      
      if (res.data.imported_count > 0) {
        toast.success(`${res.data.imported_count} venta(s) importadas`);
      }
      if (res.data.error_count > 0) {
        toast.error(`${res.data.error_count} venta(s) con errores`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const validCount = extracted.filter(e => e._status === "ok").length;
  const totalExtracted = extracted.reduce((sum, e) => sum + (e.total || 0), 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Importar Ventas de Aronium</h1>
        <p className="text-slate-500 text-sm mt-0.5">Sube tus PDFs de Aronium y los convertiremos en ventas del sistema</p>
      </div>

      {/* Info box */}
      <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm">
        <div className="font-semibold text-brand-800 mb-1 flex items-center gap-2">
          <FileText size={16} /> ¿Cómo funciona?
        </div>
        <ol className="text-brand-600 text-xs space-y-1 ml-6 list-decimal">
          <li>Selecciona uno o varios PDFs de facturas/tickets de Aronium</li>
          <li>El sistema extrae automáticamente los datos con IA</li>
          <li>Revisa y confirma antes de importar</li>
          <li>Las ventas se guardan con su folio original de referencia</li>
        </ol>
      </div>

      {/* Upload area */}
      {extracted.length === 0 && !results && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              files.length > 0 ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:border-brand-300 hover:bg-slate-50"
            }`}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={handleFiles}
            />
            <Upload size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="font-medium text-slate-700">Arrastra tus PDFs aquí o haz clic para seleccionar</p>
            <p className="text-xs text-slate-400 mt-1">Solo archivos PDF de Aronium</p>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-sm font-medium text-slate-700">{files.length} archivo(s) seleccionados:</div>
              {files.map((file, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-brand-600" />
                    <span className="text-sm text-slate-700">{file.name}</span>
                    <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button onClick={() => removeFile(i)} className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-red-500">
                    <X size={14} />
                  </button>
                </div>
              ))}
              
              <button
                onClick={processFiles}
                disabled={processing}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Procesando PDFs con IA...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Procesar {files.length} PDF(s)
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Extracted data preview */}
      {extracted.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">Vista previa de datos extraídos</h2>
              <p className="text-xs text-slate-500">{validCount} de {extracted.length} listos para importar · Total: {formatMXN(totalExtracted)}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setExtracted([]); setFiles([]); }}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600"
              >
                Cancelar
              </button>
              <button
                onClick={confirmImport}
                disabled={importing || validCount === 0}
                className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {importing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Importar {validCount} venta(s)
              </button>
            </div>
          </div>

          <div className="grid gap-3">
            {extracted.map((item, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl border p-4 shadow-sm ${
                  item._status === "error" ? "border-red-200 bg-red-50" : "border-slate-200"
                }`}
              >
                {item._status === "error" ? (
                  <div className="flex items-center gap-3">
                    <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium text-red-700">{item._file_name}</div>
                      <div className="text-sm text-red-600">{item._error}</div>
                    </div>
                    <button onClick={() => removeExtracted(i)} className="p-1.5 hover:bg-red-100 rounded text-red-400 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-slate-900">{item.folio}</span>
                          <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{item.date}</span>
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Check size={10} /> Listo
                          </span>
                        </div>
                        <div className="text-sm text-slate-600 mt-1">{item.customer_name}</div>
                        {item.customer_address && (
                          <div className="text-xs text-slate-400 mt-0.5 truncate max-w-md">{item.customer_address}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-brand-700 text-lg">{formatMXN(item.total)}</div>
                        <div className="text-xs text-slate-400">{item.items?.length || 0} productos</div>
                      </div>
                      <button onClick={() => removeExtracted(i)} className="p-1.5 hover:bg-slate-100 rounded text-slate-300 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    {/* Items preview */}
                    <div className="mt-3 bg-slate-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-slate-500 mb-2">Productos:</div>
                      <div className="space-y-1">
                        {(item.items || []).slice(0, 5).map((prod, j) => (
                          <div key={j} className="flex justify-between text-xs">
                            <span className="text-slate-600 truncate max-w-xs">{prod.product_name}</span>
                            <span className="text-slate-500">{prod.quantity} × {formatMXN(prod.unit_price)} = {formatMXN(prod.subtotal)}</span>
                          </div>
                        ))}
                        {(item.items || []).length > 5 && (
                          <div className="text-xs text-slate-400">+{item.items.length - 5} más...</div>
                        )}
                      </div>
                      {item.shipping > 0 && (
                        <div className="flex justify-between text-xs mt-2 pt-2 border-t border-slate-200">
                          <span className="text-slate-500">Envío</span>
                          <span className="text-slate-600">{formatMXN(item.shipping)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import results */}
      {results && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="text-center mb-4">
            {results.imported_count > 0 ? (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-3">
                <Check size={32} className="text-emerald-600" />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-3">
                <AlertCircle size={32} className="text-amber-600" />
              </div>
            )}
            <h3 className="text-lg font-bold text-slate-900">Importación completada</h3>
            <p className="text-sm text-slate-500">
              {results.imported_count} venta(s) importadas
              {results.error_count > 0 && ` · ${results.error_count} con errores`}
            </p>
          </div>

          {results.imported?.length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-medium text-emerald-700 mb-2">✓ Importadas correctamente:</div>
              <div className="bg-emerald-50 rounded-lg p-3 space-y-1">
                {results.imported.map((imp, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-emerald-800">{imp.folio} <span className="text-emerald-600 text-xs">(Aronium: {imp.external_folio})</span></span>
                    <span className="font-medium text-emerald-700">{formatMXN(imp.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.errors?.length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-medium text-red-700 mb-2">✗ Errores:</div>
              <div className="bg-red-50 rounded-lg p-3 space-y-1">
                {results.errors.map((err, i) => (
                  <div key={i} className="text-sm text-red-700">
                    {err.folio}: {err.error}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center">
            <button
              onClick={() => { setResults(null); setExtracted([]); setFiles([]); }}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold"
            >
              Importar más PDFs
            </button>
            <a
              href="/ventas"
              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-sm font-medium"
            >
              Ver ventas
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
